import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { useSession } from '@/lib/auth/session';

/**
 * 내 기술인 교육 현황 (`GET /api/v1/home/tech-education`, DOC-61 §6).
 *
 * ## 왜 인사 정보 훅에 얹지 않았나
 *
 * 대상이 다르다. `useHrProfile` 은 전 직원이 읽는 개인 자료이고, 이쪽은 **기술인만** 해당한다.
 * 한 응답에 담으면 해당 없는 사람에게도 매번 승급 진단이 계산돼 실려 온다 — 그 계산은 경력·서류·
 * 기준표를 함께 읽는 무거운 것이라 홈 첫 화면의 값으로는 비싸다.
 *
 * ## `technician: false` 를 빈 목록과 구분한다
 *
 * 등급이 하나도 없는 것과, 등급은 있는데 모자란 것이 없는 것은 **다른 상태**다. 후자는 「이상 없음」
 * 이고 전자는 「해당 없음」 이다. 목록만 보고 그리면 화면이 둘을 같게 그린다.
 */

export type MyTechEducation = {
  technician: boolean;
  /**
   * 다음 등급까지의 상태. **승급교육에 대해서는 아무 말도 하지 않는다** —
   * 그건 `promotionEducations` 가 협회 명부를 근거로 답한다.
   */
  promotions: Array<{
    gradeType: string;
    currentGrade: string | null;
    nextGrade: string | null;
    ready: boolean;
  }>;
  expiries: Array<{
    educationId: string;
    label: string;
    courseName: string | null;
    validUntil: string;
    expired: boolean;
  }>;
  inProgress: Array<{
    educationId: string;
    label: string;
    courseName: string | null;
    institution: string | null;
    startDate: string | null;
  }>;
  refunds: Array<{
    educationId: string;
    label: string;
    courseName: string | null;
    fee: number | null;
    refundStatus: 'NONE' | 'PENDING' | 'PAID';
    refundDueOn: string | null;
    refundPaidOn: string | null;
  }>;
  /**
   * 협회 명부가 **미이수라고 말한** 법정교육. 대개 한 줄이다
   * (「안전관리, 건축 기본교육 및 최초교육을 이수해 주세요」).
   *
   * 위의 `expiries`·`inProgress` 와 원장이 다르다 — 저 둘은 결재를 거쳐야 차는 우리 원장이고,
   * 이것은 협회가 내려 준 명부다. **협회가 정본이다**(사용자 결정 2026-09-16).
   */
  associationEducations: Notice[];
  /**
   * 승급교육 — **협회 「승급교육 이수 후 등급」 칸이 근거의 전부다**. 직무분야마다 한 줄이고,
   * 그 칸이 비어 있으면 아무 줄도 서지 않는다.
   *
   * 역량지수 기준표로는 세지 않는다(사용자 지적 2026-09-16). 법정교육 원장은 결재를 거쳐야
   * 차는 표라 대부분 비어 있어서, 그걸로 세면 **협회가 한 마디도 하지 않은 사람에게**
   * 「승급교육을 이수해 주세요」 가 떴다.
   */
  promotionEducations: Notice[];
  lastVerifiedOn: string | null;
  /**
   * 기술인 관리에서 나에게 보낸 교육 안내 — 들어야 할 것마다 한 줄 (V564). 본인이 완료 예정일을 적는다.
   * 재시작 전 서버는 이 칸을 보내지 않는다.
   */
  assigned?: AssignedEducation[];
};

export type AssignedEducation = {
  id: string;
  kind: 'PROMOTION' | 'EDUCATION' | 'REGISTER' | 'MANUAL';
  title: string;
  dueDate: string | null;
  sentOn: string;
  plannedOn: string | null;
};

/** 문장은 **서버가 만든다** — 화면이 조립하면 잇는 글자가 화면마다 달라진다. */
export type Notice = { key: string; message: string };

const PATH = '/v1/home/tech-education';

/** 안내받은 교육의 완료 예정일. 빈 값이면 지운다. */
export function usePlanTechEducation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, plannedOn }: { id: string; plannedOn: string | null }) =>
      apiFetch<unknown>(`${PATH}/notices/${id}/plan`, { method: 'PUT', body: { plannedOn } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['home-tech-education'] }),
  });
}

export function useMyTechEducation(): UseQueryResult<MyTechEducation> {
  const { data: session } = useSession();
  const userId = session?.user?.id ?? null;

  return useQuery({
    queryKey: ['home-tech-education', userId],
    queryFn: () => apiFetch<MyTechEducation>(PATH),
    enabled: !!userId,
    staleTime: 60_000,
  });
}
